import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createColor, deleteColor, fetchColors, updateColor} from "../../../../../reduxLib/colorsLib";


const initialColors = [
    { id: "natural-beige", name: "Natural Beige", hex: "#E8DCC4" },
    { id: "cream-white", name: "Cream White", hex: "#FFFEF2" },
    { id: "terracotta", name: "Terracotta", hex: "#D4735E" },
    { id: "sage-green", name: "Sage Green", hex: "#9CAF88" },
    { id: "dusty-blue", name: "Dusty Blue", hex: "#8FA5B8" },
    { id: "warm-brown", name: "Warm Brown", hex: "#8B7355" },
    { id: "charcoal-gray", name: "Charcoal Gray", hex: "#4A4A4A" },
    { id: "blush-pink", name: "Blush Pink", hex: "#E8B4B8" },
];

function normalizeHex(value) {
    let v = (value || "").trim();
    if (!v) return "#000000";
    if (!v.startsWith("#")) v = `#${v}`;
    v = v.toUpperCase();
    // allow #RGB or #RRGGBB
    if (/^#[0-9A-F]{3}$/.test(v)) {
        const r = v[1],
            g = v[2],
            b = v[3];
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (/^#[0-9A-F]{6}$/.test(v)) return v;
    return "#000000";
}

const toCreatorDTO = ({ name, hex ,tex}) => ({
    prodRelId: 0,
    canCoverManyOrders: true,
    TextilesCompany: "known",
    TextilesAvailable: 100,
    textiles:tex,
    ColorName: name,
    HexColor: hex,
});

const toUpdateDTO = ({ pcid, name, hex, tex }) => ({
    pcid,                 // 👈 το κρίσιμο
    HexColor: hex,
    ColorName: name,
    textiles: tex,

    // αν το backend θέλει και τα “σταθερά” fields, πρόσθεσέ τα:
    TextilesAvailable: 100,
    TextilesCompany: "known",
    canCoverManyOrders: true,
});


function slugify(name) {
    return (name || "color")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

function ColorsAd() {
    const dispatch = useDispatch();

    const [colorsPage, setColorsPage] = useState([]);
    const colorsListPage = useSelector(
        (state) => state.colorreducer.colorList
    );

    const [editingId, setEditingId] = useState(null);
    const [draftName, setDraftName] = useState("");
    const [draftHex, setDraftHex] = useState("#000000");
    const [draftTex, setDraftTex] = useState(0);

    useEffect(() => {
        dispatch(fetchColors());
    }, [dispatch]);

    useEffect(() => {
        if (colorsListPage && colorsListPage.length) {
            const mapped = colorsListPage.map((c) => ({
                pcid: c.pcid ?? null,
                id: c.colorName,
                name: c.colorName,
                hex: c.hexColor,
                tex: c.textiles ?? -1,
            }));
            setColorsPage(mapped);
        } else {
            setColorsPage(
                initialColors.map((x) => ({ ...x, pcid: null }))
            );
        }
    }, [colorsListPage]);

    const editing = useMemo(
        () => colorsPage.find((c) => c.id === editingId) || null,
        [colorsPage, editingId]
    );

    function openEdit(color) {
        setEditingId(color.id);
        setDraftName(color.name);
        setDraftHex(color.hex);
        //setDraftTex(color.tex ?? 0);
        console.log("Opening edit for color:", color);
        let texValue = color.tex;

        if (texValue === "bambakero") texValue = 0;
        else if (texValue === "bambakeroLourex") texValue = 1;
        else if (texValue === "forte") texValue = 2;
        else if (texValue == null) texValue = -1;

        setDraftTex(texValue);
    }

    function closeEdit() {
        setEditingId(null);
        setDraftName("");
        setDraftHex("#000000");
        setDraftTex(0);
    }

    async function saveEdit() {
        if (!editing) return;

        const nextName = (draftName || "").trim() || editing.name;
        const nextHex = normalizeHex(draftHex);
        const nextTex= draftTex;

        setColorsPage((prev) =>
            prev.map((c) =>
                (c.pcid ?? c.id) === (editing.pcid ?? editing.id)
                    ? { ...c, id: nextName, name: nextName, hex: nextHex,tex: nextTex }
                    : c
            )
        );
        console.log("EDITING", editing);
        // CREATE: στείλε CreatorDTO full, prodRelId = 0 κλπ
        if (editing.pcid != null) {
            // ✅ UPDATE
            const payload = toUpdateDTO({ pcid: editing.pcid, name: nextName, hex: nextHex,tex: nextTex });
            await dispatch(updateColor(payload));
            await dispatch(fetchColors());
        } else {
            // ✅ CREATE
            const payload = toCreatorDTO({ name: nextName, hex: nextHex,tex: nextTex });
            console.log("Creating new color with payload:", payload);
            await dispatch(createColor(payload));

            // για να πάρεις το νέο pcid στο UI, κάνε refetch (το πιο safe)
            await dispatch(fetchColors());
        }


        closeEdit();
    }

   async function removeColor(color) {
        // local (δεν υπάρχει στη βάση)
        if (!color.pcid) {
            setColorsPage(prev => prev.filter(c => c.id !== color.id));
            if (editingId === color.id) closeEdit();
            return;
        }
        // persisted (υπάρχει στη βάση)
         dispatch(deleteColor(color.pcid)).then((action) => {
            if (action.type.endsWith('fulfilled')) {
                setColorsPage(prev => prev.filter(c => c.pcid !== color.pcid));
                if (editingId === color.id) closeEdit();
            } else {
                alert("Delete failed on server");
            }
        });


    }

    function addColor() {
        const baseName = "New Color";
        const baseHex = "#CCCCCC";
        const baseId = slugify(`${baseName}-${Date.now()}`);
        const baseTex=0;

        const newColor = { id: baseId, name: baseName, hex: baseHex,tex:baseTex};
        setColorsPage((prev) => [newColor, ...prev]);
        openEdit(newColor);
    }

    function saveChanges() {
        // Replace with your API call / persistence logic
        console.log("Saving colors:", colorsPage);
        alert("Saved! (check console)");
    }

    function updateHexForCard(id, nextHex) {
        const hex = normalizeHex(nextHex);
        setColorsPage((prev) => prev.map((c) => (c.id === id ? { ...c, hex } : c)));
    }

    function updateHexTextForCard(id, nextHexText) {
        // Let user type freely; normalize only if it becomes valid
        const raw = (nextHexText || "").trim();
        let candidate = raw;
        if (candidate && !candidate.startsWith("#")) candidate = `#${candidate}`;
        const isValid = /^#[0-9a-fA-F]{3}$/.test(candidate) || /^#[0-9a-fA-F]{6}$/.test(candidate);

        setColorsPage((prev) =>
            prev.map((c) => {
                if (c.id !== id) return c;
                return { ...c, hex: isValid ? normalizeHex(candidate) : c.hex };
            })
        );
    }

    return (
        <div id="colors-content" className="p-8 bg-cream font-script">
            <div
                id="primary-colors-section"
                className="bg-white rounded-lg shadow p-6 mb-8"
            >
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-serif font-bold text-custom-green">
                            Product Textile Colors
                        </h2>
                        <p className="text-gray-600 text-sm mt-1">
                            Manage available colors for handmade textile baskets
                        </p>
                    </div>

                    <div className="flex space-x-3">
                        <button
                            type="button"
                            onClick={addColor}
                            className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-200"
                        >
                            <i className="fa-solid fa-plus mr-2" />
                            Add Color
                        </button>

                        <button
                            type="button"
                            onClick={saveChanges}
                            className="bg-custom-green text-white px-6 py-2 rounded-lg font-medium hover:bg-opacity-90"
                        >
                            <i className="fa-solid fa-save mr-2" />
                            Save Changes
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {colorsPage.map((c) => (
                        <div
                            key={c.pcid ?? c.id}
                            id={`${c.id}-card`}
                            className="border rounded-lg p-4 relative group"
                        >
                            <div className="absolute top-2 right-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    type="button"
                                    onClick={() => openEdit(c)}
                                    className="bg-blue-500 text-white w-8 h-8 rounded-lg hover:bg-blue-600 flex items-center justify-center"
                                    aria-label={`Edit ${c.name}`}
                                >
                                    <i className="fa-solid fa-edit text-xs" />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => removeColor(c)}
                                    className="bg-red-500 text-white w-8 h-8 rounded-lg hover:bg-red-600 flex items-center justify-center"
                                    aria-label={`Delete ${c.name}`}
                                >
                                    <i className="fa-solid fa-trash text-xs" />
                                </button>
                            </div>

                            <div className="flex items-center mb-3">
                                <div
                                    className={`w-12 h-12 rounded-lg mr-3 shadow-sm ${
                                        c.hex.toUpperCase() === "#FFFEF2" ? "border" : ""
                                    }`}
                                    style={{ backgroundColor: c.hex }}
                                />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {c.name}
                                    </label>
                                    <span className="text-xs text-gray-500 font-mono">
                    {normalizeHex(c.hex)}
                  </span>
                                </div>
                            </div>

                            <div
                                className="color-swatch mb-3 h-10 rounded-lg border"
                                style={{ backgroundColor: c.hex }}
                            />

                            <input
                                type="color"
                                value={c.hex}
                                onChange={(e) => updateHexForCard(c.id, e.target.value)}
                                className="color-input w-full h-10 rounded-lg border"
                                aria-label={`Pick color for ${c.name}`}
                            />

                            <input
                                type="text"
                                value={c.hex.toUpperCase()}
                                onChange={(e) => updateHexTextForCard(c.id, e.target.value)}
                                className="w-full mt-2 px-3 py-2 border rounded-lg text-sm font-mono"
                                aria-label={`Hex value for ${c.name}`}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Simple modal editor */}
            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={closeEdit}
                        aria-hidden="true"
                    />
                    <div className="relative w-[95%] max-w-md bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-serif font-bold text-gray-900">
                                    Edit Color
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Update the name and hex value.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeEdit}
                                className="text-gray-500 hover:text-gray-700"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="mt-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Name
                                </label>
                                <input
                                    value={draftName}
                                    onChange={(e) => setDraftName(e.target.value)}
                                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                                    placeholder="Color name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Textiles
                                </label>
                                <select
                                    name="textiles"
                                    value={draftTex}
                                    onChange={(e) => setDraftTex(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                                >
                                    <option value={-1}>None</option>
                                    <option value={0}>bambakero</option>
                                    <option value={1}>bambakeroLourex</option>
                                    <option value={2}>forte</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Με βάση αυτό θα εμφανίζονται τα διαθέσιμα colors.
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-3 items-end">
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Pick
                                    </label>
                                    <input
                                        type="color"
                                        value={normalizeHex(draftHex)}
                                        onChange={(e) => setDraftHex(e.target.value)}
                                        className="w-full h-10 mt-1 rounded-lg border"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Hex
                                    </label>
                                    <input
                                        value={draftHex}
                                        onChange={(e) => setDraftHex(e.target.value)}
                                        className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono"
                                        placeholder="#RRGGBB"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-10 h-10 rounded-lg border"
                                        style={{ backgroundColor: normalizeHex(draftHex) }}
                                    />
                                    <span className="text-sm text-gray-600 font-mono">
                    {normalizeHex(draftHex)}
                  </span>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={closeEdit}
                                        className="px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={saveEdit}
                                        className="px-4 py-2 rounded-lg bg-custom-green text-white hover:bg-opacity-90"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

}

export default ColorsAd;